import { useBreakpointValue } from "@gluestack-style/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import {
  IModalOption,
  IModalTemplateType
} from "@repo/core/components/layout/ModalTemplate/types";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { apiInstance } from "@repo/core/services/instances/api";
import { TransactionsResource } from "@repo/core/services/resources/transactions";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SelectTransactionItemProps } from "@repo/core/components/core/SelectTransactionsList/components/SelectTransactionItem/types";
import { Z } from "@repo/core/utils/zod.utils";
import { IUserConfirmData } from "./components/AuthorizationModal/types";
import {
  CancelTransactionsInputDTO,
  TransactionItemModel
} from "@repo/core/services/entities/Transactions";
import { getCurrentDateTime } from "@repo/core/utils/date.utils";
import {
  TransactionItemStatusEnum,
  TransactionItemTypeEnum,
  transactionTypeLetter
} from "@repo/core/constants/transactions";
import useToast from "@repo/core/components/core/Toast";
import { groupTransactionsByAuthorizatonId } from "@repo/core/utils/transactions.utils";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { operationTypeEnum } from "../CancelTransactionOptions/types";

const transactionsResource = new TransactionsResource(apiInstance);

const formSchema = Z.object({
  // nsuTerminal: Z.string({ message: "Campo obrigatório" }),
  // lngNsuCaptura: Z.string({ message: "Campo obrigatório" }),
  lngAutorizacao: Z.number({ message: "Campo obrigatório" })
});

export type FormDataType = z.infer<typeof formSchema>;
export const CancelTransactionController = () => {
  const toast = useToast();
  const { operationType } = useRouteParams();
  const [selectedTransaction, setSelectedTransaction] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const authorizationModalRef = useRef<IModalRefProps>(null);
  const alertModalRef = useRef<IModalRefProps>(null);

  const { back, replace } = useNavigation();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const {
    data: transactionsData,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ["findTransactionsDay"],
    queryFn: () => transactionsResource.findTransactions()
  });

  const transactionsOptions: SelectTransactionItemProps[] = useMemo(() => {
    let transactions: TransactionItemModel[] = transactionsData?.data || [];

    if (operationType === operationTypeEnum.PURCHASE_WITHDRAWAL) {
      transactions = transactions.filter(
        (item) =>
          (item.strTipo === TransactionItemTypeEnum.COMPRA ||
            item.strTipo === TransactionItemTypeEnum.SAQUE) &&
          item.strSituacao !== TransactionItemStatusEnum.CANCELADO
      );
    } else {
      transactions = transactions.filter(
        (item) =>
          item.strTipo === TransactionItemTypeEnum.PONTUACAO &&
          item.strSituacao !== TransactionItemStatusEnum.CANCELADO
      );

      transactions = groupTransactionsByAuthorizatonId(transactions);
    }

    return (
      transactions?.map((item) => ({
        authorizationId: item.lngAutorizacao!,
        card: `****${item.strCartaoPlastico?.split(".")[3]}`,
        operation: item.strTipo!,
        value: item.dblValor!
      })) || ([] as SelectTransactionItemProps[])
    );
  }, [transactionsData, operationType]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid }
  } = useForm<FormDataType>({
    resolver: zodResolver(formSchema)
  });

  const handleOnSubmitForm = handleSubmit(() => {
    authorizationModalRef.current?.open();
  });
  const handleOnChange = (value: string) => {
    setSelectedTransaction(value);
    setValue("lngAutorizacao", parseInt(value), {
      shouldValidate: true
    });
  };

  const handleGoBack = () => {
    replace(ROUTES.sales);
  };
  const handleGoToProof = () => {
    // TODO: go to proof of transaction
    // replace(ROUTES.transactions);
  };

  const handleOnAuthorizationConfirm = async (
    objUsuarioResponsavel: IUserConfirmData
  ) => {
    try {
      setIsLoading(true);
      const formData = getValues();

      const selectedTransactions = transactionsOptions.find(
        (item) => item?.authorizationId === formData.lngAutorizacao
      );

      const { datDataLocal, strHoraLocal } = getCurrentDateTime();

      const lngNsuCaptura = parseInt(strHoraLocal.replace(/:/g, "")) - 1;
      const dblValorCompra = parseFloat(
        selectedTransactions?.value?.toFixed(2) || "0.00"
      );

      const payload: CancelTransactionsInputDTO = {
        dblValorCompra,
        strTipoOperacao:
          transactionTypeLetter[
            selectedTransactions?.operation! as TransactionItemTypeEnum
          ],
        lngAutorizacao: formData.lngAutorizacao,
        lngNsuCaptura,
        datDataLocal,
        strHoraLocal,
        objUsuarioResponsavel
      };

      const { data: cancelData } =
        await transactionsResource.cancelTransaction(payload);

      if (cancelData.blnSucesso) {
        authorizationModalRef.current?.close();
        alertModalRef.current?.open();
        refetch();
      } else {
        toast.error("Erro ao cancelar transação");
      }
    } catch (error) {
      console.error("request error", error);
      toast.error(
        "Erro ao cancelar transação, verifique os dados e tente novamente"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const alertOptions: IModalOption[] = [
    {
      label: "Voltar ao início",
      type: IModalTemplateType.success,
      onPress: handleGoBack
    },
    {
      label: "Ver comprovante",
      type: IModalTemplateType.outline,
      onPress: handleGoToProof
    }
  ];

  return {
    back,
    transactionsOptions,
    isMobile,
    selectedTransaction,
    handleOnChange,
    control,
    authorizationModalRef,
    handleOnAuthorizationConfirm,
    alertModalRef,
    isFetching,
    isValid,
    alertOptions,
    handleOnSubmitForm,
    isLoading
  };
};
